import axios, { AxiosError } from "axios";
import axiosRetry from "axios-retry";
import ApiService from "./Services/ApiService";
import { headersConfigs } from "./ApiConfigs/ApiHeadersConfigs";
import {
    RESPONSE_CODE_LIMIT_EXCEEDED,
    RESPONSE_CODE_PRIVACY_POLICY_UNACCEPTED,
    RESPONSE_CODE_PROFILE_DEACTIVATED,
    RESPONSE_CODE_PROFILE_SUSPENDED,
} from "@infrastructure/BackendClientCodes";
import { UserState, useUserStore } from "@infrastructure/Persistence/UserState";
import { startUserActivation } from "@shared/startUserActivationProcess";
import { SHOW_LIMIT_EXCEEDED_TYPE } from "../Presentation/Components/Modals/ServiceModals/GeneralErrorsModal/GeneralErrorsModal.const";
import { publish } from "@infrastructure/Events/EventBus";
import { TippspielApi } from "../api/tippspiel";
import { ChecktokApi } from "../api/checktok";
import { CommunityApi } from "../api/community";
import { TruckTourApi } from "../api/truck";

const DEFAULT_HEADERS = {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
    "app-v": headersConfigs.appVersion,
    "App-Timezone": headersConfigs.appTimezone,
};

const FAILED_REQUEST_RETRY_COUNT = 6;
const TIMEOUT = 30000;
const FORM_CLIENT_TIMEOUT = 300000;
const CREDENTIALS = true;
const RESPONSE_TYPE = "json";

export const clientBase = axios.create({
    baseURL: ApiService.REACT_APP_API_ENDPOINT_CACHED,
    withCredentials: CREDENTIALS,
    headers: DEFAULT_HEADERS,
    responseType: RESPONSE_TYPE,
    timeout: TIMEOUT,
});

axiosRetry(clientBase, {
    retries: FAILED_REQUEST_RETRY_COUNT,
    retryCondition: axiosRetry.isNetworkError,
    retryDelay: (retryCount, error) =>
        axiosRetry.exponentialDelay(retryCount, error, 1000),
});

export const client = axios.create({
    baseURL: ApiService.REACT_APP_API_ENDPOINT_CACHED,
    withCredentials: CREDENTIALS,
    headers: DEFAULT_HEADERS,
    responseType: RESPONSE_TYPE,
    timeout: TIMEOUT,
});

axiosRetry(client, {
    retries: FAILED_REQUEST_RETRY_COUNT,
    retryCondition: axiosRetry.isNetworkError,
    retryDelay: (retryCount, error) =>
        axiosRetry.exponentialDelay(retryCount, error, 1000),
});

export const clientWithoutCache = axios.create({
    baseURL: ApiService.BASE_URL,
    withCredentials: CREDENTIALS,
    headers: DEFAULT_HEADERS,
    responseType: RESPONSE_TYPE,
    timeout: TIMEOUT,
});

axiosRetry(clientWithoutCache, {
    retries: FAILED_REQUEST_RETRY_COUNT,
    retryCondition: axiosRetry.isNetworkError,
    retryDelay: (retryCount, error) =>
        axiosRetry.exponentialDelay(retryCount, error, 1000),
});

export const formClient = axios.create({
    baseURL: ApiService.BASE_URL,
    withCredentials: CREDENTIALS,
    headers: {
        ...DEFAULT_HEADERS,
        "Content-Type": "multipart/form-data",
    },
    responseType: RESPONSE_TYPE,
    timeout: FORM_CLIENT_TIMEOUT,
});

export type HandledAxiosError = AxiosError & { final: boolean | undefined };

const { unauthenticate, setUserState } = useUserStore.getState();
const interceptorErrorCb = (error: AxiosError): Promise<HandledAxiosError> => {
    const status = error.response?.status;
    let final = undefined;
    switch (status) {
        case RESPONSE_CODE_PRIVACY_POLICY_UNACCEPTED:
            setUserState(UserState.PROFILE_STATUS_NOT_ONBOARDED);
            startUserActivation();
            final = false;
            break;
        case RESPONSE_CODE_PROFILE_DEACTIVATED:
            setUserState(UserState.PROFILE_STATUS_DEACTIVATED);
            startUserActivation();
            final = false;
            break;
        case RESPONSE_CODE_PROFILE_SUSPENDED:
            setUserState(UserState.PROFILE_STATUS_SUSPENDED);
            startUserActivation();
            final = true;
            break;
        case 551:
        case 550:
        case 401:
            if (
                useUserStore.getState().userState ===
                    UserState.PROFILE_STATUS_ACTIVATED ||
                useUserStore.getState().userState ===
                    UserState.PROFILE_STATUS_UNKNOWN
            ) {
                unauthenticate();
            }
            startUserActivation();

            if (error.response) {
                error.response.status = 401;
            }
            final = false;
            break;
        case RESPONSE_CODE_LIMIT_EXCEEDED:
            publish({ name: SHOW_LIMIT_EXCEEDED_TYPE });
            final = false;
            break;
    }

    return Promise.reject({ ...error, final });
};

client.interceptors.response.use(undefined, interceptorErrorCb);

export const apiClientTippspiel = new TippspielApi(
    undefined,
    ApiService.BASE_URL,
    client,
);
export const apiClientChecktok = new ChecktokApi(
    undefined,
    ApiService.BASE_URL,
    client,
);
export const apiClientCommunity = new CommunityApi(
    undefined,
    ApiService.BASE_URL,
    client,
);

export const apiClientTruckTour = new TruckTourApi(
    undefined,
    ApiService.BASE_URL,
    client,
);
